// src/stores/player-store.ts
// Single source of truth for the unified persistent player.
// Limeplay's <VideoPlayer> (mounted once in the locale layout) is DRIVEN by
// this store; Limeplay events are written back through `PlayerBridge`.
"use client";

import { create } from "zustand";
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from "zustand/middleware";

import { pauseCurrentMedia } from "@/hooks/useMediaManager";
import { track } from "@/lib/player-analytics";
import type {
  AssetLoadContextLike,
  PlayerAsset,
} from "@/stores/player-types";

export type { PlayerAsset } from "@/stores/player-types";

/** Imperative handles registered by PlayerBridge (lives inside <VideoPlayer>). */
export interface PlayerControls {
  play: () => Promise<void>;
  pause: () => void;
  seek: (t: number) => void;
  setRate: (r: number) => void;
  setVolume: (v: number) => void;
}

let controls: PlayerControls | null = null;

export interface PlayerState {
  // ── runtime (not persisted)
  current: PlayerAsset | null;
  queue: PlayerAsset[];
  queueIndex: number;
  isPlaying: boolean;
  isBuffering: boolean;
  currentTime: number;
  duration: number;
  /** false until the user presses play → restored sessions don't hit the network. */
  loadRequested: boolean;
  /** Start playing as soon as the source finishes loading. */
  pendingPlay: boolean;
  /** Seek target applied right after load. */
  startAt: number;
  expanded: boolean;
  hydrated: boolean;

  // ── persisted
  lastPlayedAsset: PlayerAsset | null;
  positionById: Record<string, number>;
  speed: number;
  volume: number;

  // ── actions
  play: (
    asset: PlayerAsset,
    opts?: { queue?: PlayerAsset[]; startAt?: number; paused?: boolean },
  ) => void;
  toggle: () => void;
  pause: () => void;
  resume: () => void;
  seek: (t: number) => void;
  skip: (delta: number) => void;
  next: () => void;
  prev: () => void;
  setSpeed: (r: number) => void;
  setVolume: (v: number) => void;
  setExpanded: (v: boolean) => void;
  close: () => void;
  resolveSource: (
    ctx: AssetLoadContextLike,
  ) => Promise<string | undefined>;

  // ── internal (bridge → store)
  _registerControls: (c: PlayerControls | null) => void;
  _report: (p: Partial<PlayerState>) => void;
  _restore: () => void;
  _ended: () => void;
  _tick: (time: number, duration: number) => void;
  _playbackEvent: (kind: "pause" | "heartbeat") => void;
}

/** localStorage writes are coalesced so timeupdate never thrashes storage. */
function throttledStorage(ms: number): StateStorage {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: { name: string; value: string } | null = null;
  const flush = () => {
    timer = null;
    if (!pending) return;
    try {
      localStorage.setItem(pending.name, pending.value);
    } catch {
      /* storage unavailable */
    }
    pending = null;
  };
  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flush();
    });
  }
  return {
    getItem: (name) => {
      try {
        return localStorage.getItem(name);
      } catch {
        return null;
      }
    },
    setItem: (name, value) => {
      pending = { name, value };
      if (!timer) timer = setTimeout(flush, ms);
    },
    removeItem: (name) => {
      try {
        localStorage.removeItem(name);
      } catch {
        /* ignore */
      }
    },
  };
}

const END_GUARD_SECONDS = 5;

export const usePlayerStore = create<PlayerState>()(
  persist(
    (set, get) => ({
      current: null,
      queue: [],
      queueIndex: 0,
      isPlaying: false,
      isBuffering: false,
      currentTime: 0,
      duration: 0,
      loadRequested: false,
      pendingPlay: false,
      startAt: 0,
      expanded: false,
      hydrated: false,

      lastPlayedAsset: null,
      positionById: {},
      speed: 1,
      volume: 1,

      play: (asset, opts) => {
        const s = get();
        const paused = opts?.paused ?? false;

        // Same asset already loaded → just resume.
        if (s.current?.id === asset.id && s.loadRequested && !opts?.startAt) {
          if (!paused) get().resume();
          return;
        }

        if (!paused) pauseCurrentMedia(); // stop inline <video>/<audio>

        const queue = opts?.queue ?? [asset];
        const idx = Math.max(
          0,
          queue.findIndex((a) => a.id === asset.id),
        );
        const saved = s.positionById[asset.id] ?? 0;
        const startAt = opts?.startAt ?? saved;

        set({
          current: asset,
          queue,
          queueIndex: idx,
          lastPlayedAsset: asset, // updated on EVERY play()
          loadRequested: true,
          pendingPlay: !paused,
          startAt,
          currentTime: startAt,
          duration: 0,
          isPlaying: false,
          isBuffering: !paused,
        });
        if (!paused) track("play", asset.id, startAt);
      },

      toggle: () => (get().isPlaying ? get().pause() : get().resume()),

      pause: () => {
        controls?.pause();
        set({ pendingPlay: false });
      },

      resume: () => {
        const s = get();
        if (!s.current) return;
        pauseCurrentMedia();
        if (!s.loadRequested) {
          // restored session: first user press loads the source
          set({
            loadRequested: true,
            pendingPlay: true,
            startAt: s.positionById[s.current.id] ?? s.startAt,
            isBuffering: true,
          });
          track("play", s.current.id, s.startAt);
          return;
        }
        set({ pendingPlay: true });
        controls?.play().catch(() => set({ isPlaying: false, pendingPlay: false }));
      },

      seek: (t) => {
        const from = get().currentTime;
        controls?.seek(t);
        set({ currentTime: t });
        const id = get().current?.id;
        if (id) track("seek", id, t, { from });
      },

      skip: (delta) => {
        const { currentTime, duration } = get();
        const max = duration || Number.MAX_SAFE_INTEGER;
        get().seek(Math.min(max, Math.max(0, currentTime + delta)));
      },

      next: () => {
        const { queue, queueIndex } = get();
        const n = queue[queueIndex + 1];
        if (n) get().play(n, { queue });
      },

      prev: () => {
        const { queue, queueIndex, currentTime } = get();
        if (currentTime > 3) return get().seek(0);
        const p = queue[queueIndex - 1];
        if (p) get().play(p, { queue });
      },

      setSpeed: (r) => {
        controls?.setRate(r);
        set({ speed: r });
      },

      setVolume: (v) => {
        controls?.setVolume(v);
        set({ volume: v });
      },

      setExpanded: (expanded) => set({ expanded }),

      close: () => {
        controls?.pause();
        set({
          current: null,
          queue: [],
          queueIndex: 0,
          isPlaying: false,
          loadRequested: false,
          pendingPlay: false,
          expanded: false,
          currentTime: 0,
          duration: 0,
          lastPlayedAsset: null,
        });
      },

      /**
       * Signed-URL hook. Called by Limeplay (`loading.resolveSource`) — never
       * from components. Limeplay aborts `signal` when the track changes, so
       * a stale resolution can never replace the current one.
       */
      resolveSource: async ({ asset, signal }) => {
        if (signal.aborted) throw new DOMException("Aborted", "AbortError");
        // TODO(signed CDN): fetch a signed URL here, e.g.
        //   const r = await fetch(`/api/media/sign?id=${asset.id}`, { signal });
        //   return (await r.json()).url;
        return asset.src;
      },

      _registerControls: (c) => {
        controls = c;
      },

      _report: (p) => set(p),

      _restore: () => {
        const s = get();
        if (s.current || !s.lastPlayedAsset) return;
        const a = s.lastPlayedAsset;
        const pos = s.positionById[a.id] ?? 0;
        // Restore PAUSED, source not loaded yet (no autoplay with sound).
        set({
          current: a,
          queue: [a],
          queueIndex: 0,
          loadRequested: false,
          pendingPlay: false,
          startAt: pos,
          currentTime: pos,
          isPlaying: false,
        });
      },

      _tick: (time, duration) => {
        const s = get();
        const id = s.current?.id;
        const patch: Partial<PlayerState> = { currentTime: time };
        if (duration && duration !== s.duration) patch.duration = duration;
        // Persisted position: only every ~2s (storage writes are throttled too).
        if (id && time > 0 && Math.abs(time - (s.positionById[id] ?? 0)) >= 2) {
          patch.positionById = { ...s.positionById, [id]: time };
        }
        set(patch);
      },

      _playbackEvent: (kind) => {
        const s = get();
        if (!s.current) return;
        if (kind === "pause" && s.current.id) {
          // flush exact position on pause
          set({
            positionById: { ...s.positionById, [s.current.id]: s.currentTime },
          });
        }
        track(kind, s.current.id, s.currentTime);
      },

      _ended: () => {
        const s = get();
        const id = s.current?.id;
        if (id) {
          const rest = { ...s.positionById };
          delete rest[id]; // reset on complete
          set({ positionById: rest, isPlaying: false, currentTime: 0 });
          track("complete", id, s.duration);
        }
        if (s.queue[s.queueIndex + 1]) get().next();
      },
    }),
    {
      name: "sharifnama-player",
      version: 1,
      storage: createJSONStorage(() => throttledStorage(1500)),
      skipHydration: true, // hydrate on mount → no SSR/CSR mismatch
      partialize: (s) => ({
        lastPlayedAsset: s.lastPlayedAsset,
        positionById: s.positionById,
        speed: s.speed,
        volume: s.volume,
      }),
      onRehydrateStorage: () => (state) => {
        state?._report({ hydrated: true });
        state?._restore();
      },
    },
  ),
);

/** Near-the-end positions restart from 0 instead of resuming on the last second. */
export function normalizeStart(pos: number, duration: number): number {
  if (!duration) return pos;
  return pos > duration - END_GUARD_SECONDS ? 0 : pos;
}
